import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Role } from "@/lib/me";

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: Role | null;
  deactivated: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  hasSignedIn: boolean;
}

export interface AuditEntry {
  id: string;
  action: string;
  detail: Record<string, unknown> | null;
  at: string;
  actorEmail: string | null;
}

const USERS = ["admin", "users"] as const;
const AUDIT = ["admin", "audit"] as const;

export function useAdminUsers() {
  return useQuery({
    queryKey: USERS,
    queryFn: () => api.get<{ users: AdminUser[]; clerkLive: boolean }>("/admin/users"),
  });
}

/** An app kept off the directory grid — see `GET /admin/apps`. */
export interface AdminApp {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  iconPath: string | null;
  url: string;
  category: string;
}

export function useAdminApps() {
  return useQuery({
    queryKey: ["admin", "apps"],
    queryFn: () => api.get<{ apps: AdminApp[] }>("/admin/apps"),
    select: (d) => d.apps,
  });
}

/** One grid app and whether it is listed under "Apps you can request". */
export interface AppVisibility {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  iconPath: string | null;
  category: string;
  openToAll: boolean;
  requestable: boolean;
}

const VISIBILITY = ["admin", "app-visibility"] as const;

/** ⚠ Superadmin only — the API refuses anyone else, whatever the UI offers. */
export function useAppVisibility() {
  return useQuery({
    queryKey: VISIBILITY,
    queryFn: () => api.get<{ apps: AppVisibility[] }>("/admin/app-visibility"),
    select: (d) => d.apps,
  });
}

export function useSetRequestable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, requestable }: { id: string; requestable: boolean }) =>
      api.put(`/admin/app-visibility/${id}`, { requestable }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: VISIBILITY });
      void qc.invalidateQueries({ queryKey: AUDIT });
      // The superadmin's own directory reads the same flag.
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

export function useAuditLog() {
  return useQuery({
    queryKey: AUDIT,
    queryFn: () => api.get<{ entries: AuditEntry[] }>("/admin/audit?limit=100"),
    select: (d) => d.entries,
  });
}

/** Shared invalidation: any admin write can change both the list and the trail. */
function useAdminMutation<TVars>(fn: (v: TVars) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS });
      void qc.invalidateQueries({ queryKey: AUDIT });
    },
  });
}

export function useCreateUser() {
  return useAdminMutation((v: { email: string; name?: string; role: Role }) =>
    api.post("/admin/users", v),
  );
}

export function useSetRole() {
  return useAdminMutation((v: { id: string; role: Role }) =>
    api.patch(`/admin/users/${v.id}/role`, { role: v.role }),
  );
}

export function useSetSuspended() {
  return useAdminMutation((v: { id: string; suspended: boolean }) =>
    api.patch(`/admin/users/${v.id}/suspend`, { suspended: v.suspended }),
  );
}

/** Mirrors ANNOUNCEMENT_LIMITS in api/src/announcements/input.ts, which enforces them. */
export const ANNOUNCEMENT_LIMITS = { title: 120, body: 2000 } as const;

export interface Announcement {
  id: string;
  title: string | null;
  body: string;
  level: "info" | "warning";
  startsAt: string;
  endsAt: string | null;
  authorEmail?: string | null;
}

export function useLiveAnnouncements() {
  return useQuery({
    queryKey: ["announcements"],
    queryFn: () => api.get<{ announcements: Announcement[] }>("/announcements"),
    select: (d) => d.announcements,
  });
}

export function useAllAnnouncements() {
  return useQuery({
    queryKey: ["announcements", "all"],
    queryFn: () => api.get<{ announcements: Announcement[] }>("/announcements/all"),
    select: (d) => d.announcements,
  });
}

export function useAnnouncementMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["announcements"] });
  };
  return {
    create: useMutation({
      mutationFn: (v: { title: string; body: string; level: "info" | "warning" }) =>
        api.post("/announcements", v),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/announcements/${id}`),
      onSuccess: invalidate,
    }),
    dismiss: useMutation({
      mutationFn: (id: string) => api.post(`/announcements/${id}/dismiss`),
      onSuccess: invalidate,
    }),
  };
}

export function useUpdateName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.patch<{ ok: boolean; name: string | null; warning: string | null }>("/me", {
        name,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

// --- Daily digest (superadmin) ----------------------------------------------

export interface DigestSettings {
  recipients: string[];
  enabled: boolean;
  problems: string[];
  schedule: string;
  maxAttempts: number;
}

export interface DigestRun {
  day: string;
  status: "running" | "sent" | "failed";
  attempts: number;
  startedAt: string;
  finishedAt: string | null;
  verdict: "ok" | "warn" | "fail" | null;
  subject: string | null;
  recipients: string | null;
  error: string | null;
  headline: {
    people: number;
    newPeople: number;
    signIns: number;
    launches: number;
    adminActions: number;
    checks: { ok: number; warn: number; fail: number };
  } | null;
}

export function useDigest() {
  return useQuery({
    queryKey: ["admin", "digest"],
    queryFn: () => api.get<{ settings: DigestSettings; runs: DigestRun[] }>("/admin/digest"),
  });
}

export function useDigestActions() {
  const qc = useQueryClient();
  return {
    /** Builds the email as it would read now. Sends nothing. */
    preview: useMutation({
      mutationFn: () => api.get<{ subject: string; html: string; text: string }>("/admin/digest/preview"),
    }),
    /** Sends the last 24 hours now. Audited; does not use up the 07:00 send. */
    send: useMutation({
      mutationFn: () => api.post<{ ok: true; subject: string }>("/admin/digest/send"),
      onSuccess: () => void qc.invalidateQueries({ queryKey: AUDIT }),
    }),
  };
}
